import React, { useEffect, useMemo, useRef, useState } from 'react';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandOutlinedIcon from '@mui/icons-material/ExpandOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import ListItemLink from 'components/Mui/list-item-link';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import {
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Toolbar,
  Box,
  Typography
} from '@mui/material';
import { useLocalStorageState } from 'shared/hooks/local-storage-state';
import { Drawer } from 'shared/ui/drawer';
import { menuItems, MenuItemType, MenuBaseItem } from './menu-items';

const ACTIVE_ITEM_BACKGROUND = 'linear-gradient(90deg, rgba(93,108,176,0.95) 0%, rgba(111,135,195,0.95) 100%)';
const COLLAPSED_ACTIVE_MARGIN = '4px';
const SEARCH_MARK_BG = 'rgba(255, 213, 79, 0.35)';
const SEARCH_ACTIVE_MARK_BG = '#FFD54F';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const titleMatches = (item: MenuItemType, query: string) => item.title.toLowerCase().includes(query);
const keywordsMatches = (item: MenuItemType, query: string) => item.keywords ? item.keywords.some((x) => x.toLowerCase().includes(query)) : false;

const itemMatches = (item: MenuItemType, query: string) =>
  titleMatches(item, query) || keywordsMatches(item, query);

const filterItems = (items: MenuItemType[], query: string): MenuItemType[] =>
  items.reduce<MenuItemType[]>((acc, item) => {
    if (itemMatches(item, query)) {
      acc.push(item);
      return acc;
    }
    if (item.subItems) {
      const subItems = filterItems(item.subItems, query) as unknown[] as MenuBaseItem[];
      if (subItems.length) acc.push({ ...item, subItems });
    }
    return acc;
  }, []);

const collectMatches = (items: MenuItemType[], query: string, parentMatched = false): MenuItemType[] =>
  items.flatMap((item) => {
    const matched = parentMatched || itemMatches(item, query);
    if (item.subItems) return collectMatches(item.subItems, query, matched);
    return item.to && matched ? [item] : [];
  });

const HighlightedText = ({ text, query, active }: { text: string; query: string; active?: boolean }) => {
  if (!query) return <span>{text}</span>;
  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <Box
            key={i}
            component="mark"
            sx={{
              background: active ? SEARCH_ACTIVE_MARK_BG : SEARCH_MARK_BG,
              color: active ? '#1a1a1a' : 'inherit',
              borderRadius: '3px',
              px: '1px'
            }}
          >
            {part}
          </Box>
        ) : (
          part
        )
      )}
    </span>
  );
};

type Props = {
  open: boolean;
  isPinnedOpen: boolean;
  onPin: () => void;
  onClose: () => void;
  onHoverChange: (isHovering: boolean) => void;
};

export const SideMenu = ({ open, isPinnedOpen, onPin, onClose, onHoverChange }: Props) => {
  const [allSectionsOpen, setAllSectionsOpen] = useLocalStorageState('all-menu-open', false);
  const [openSections, setOpenSections] = useLocalStorageState<{ [key: string]: boolean }>('sections', {});

  const [itemsSearch, setItemsSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const itemsRef = useRef<Record<string, HTMLElement | null>>({});
  const location = useLocation();
  const navigate = useNavigate();

  const query = itemsSearch.trim().toLowerCase();
  const isSearching = open && query.length > 0;

  const visibleItems = useMemo(() => (isSearching ? filterItems(menuItems, query) : menuItems), [isSearching, query]);
  const searchResults = useMemo(
    () => (isSearching ? collectMatches(visibleItems, query) : []),
    [isSearching, visibleItems, query]
  );
  const activeKey = searchResults[activeIndex]?.to;

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (activeKey) itemsRef.current[activeKey]?.scrollIntoView({ block: 'nearest' });
  }, [activeKey]);

  const isSectionOpen = (title: string) => (isSearching ? true : !!openSections[title]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && searchResults.length) {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % searchResults.length);
    } else if (e.key === 'ArrowUp' && searchResults.length) {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + searchResults.length) % searchResults.length);
    } else if (e.key === 'Enter' && activeKey) {
      navigate(activeKey);
      setItemsSearch('');
    } else if (e.key === 'Escape') {
      setItemsSearch('');
    }
  };

  const getCollapsedActiveStyles = (isItemActive: boolean) =>
    !open && isItemActive
      ? {
          background: 'transparent',
          position: 'relative',
          overflow: 'visible',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            margin: `0 ${COLLAPSED_ACTIVE_MARGIN}`,
            borderRadius: '8px',
            background: ACTIVE_ITEM_BACKGROUND,
            zIndex: -1,
          },
        }
      : {};

  const toggleSection = (title: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const toggleAllSections = () => {
    const newState = !allSectionsOpen;
    setAllSectionsOpen(newState);

    const updateOpenState = (items: MenuItemType[]) => {
      return items.reduce(
        (acc, item) => {
          acc[item.title] = newState;
          if (item.subItems) {
            Object.assign(acc, updateOpenState(item.subItems));
          }
          return acc;
        },
        {} as Record<string, boolean>,
      );
    };
    const updatedSections = updateOpenState(menuItems);
    setOpenSections(updatedSections);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const renderMenuItems = (items: MenuItemType[], depth = 0) => {
    return items.map((item) => {
      const isNestedItem = depth > 1;
      const leftPadding = open ? '6px' : '0px';
      const justifyContent = open ? 'flex-start' : 'center';
      const iconSpacing = open ? '8px' : 0;
      const groupOpen = !!item.subItems && isSectionOpen(item.title);
      const isActiveItem = !item.subItems && isActive(item.to ?? '');
      const isSearchActive = isSearching && !item.subItems && !!item.to && item.to === activeKey;
      const collapsedActiveStyles = getCollapsedActiveStyles(isActiveItem);
      const highlightQuery = isSearching ? query : '';
      const wrapperSx = {
        px: open ? '4px' : 0,
        py: '4px',
        mb: '4px',
        ...(item.subItems && groupOpen
          ? {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(40px)',
              WebkitBackdropFilter: 'blur(40px)',
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px',
            }
          : {}),
      };

      return (
        <Box
          key={item.title}
          sx={wrapperSx}
          ref={(el: HTMLElement | null) => {
            if (item.to && !item.subItems) itemsRef.current[item.to] = el;
          }}
        >
          {item.subItems ? (
            <>
              <ListItemButton
                onClick={() => toggleSection(item.title)}
                sx={{
                  pl: leftPadding,
                  pr: open ? '6px' : 0,
                  justifyContent,
                  background: open ? (isActiveItem ? ACTIVE_ITEM_BACKGROUND : 'transparent') : 'transparent',
                  py: '6px',
                  borderRadius: '8px',
                  ...collapsedActiveStyles,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 'auto',
                    marginRight: iconSpacing,
                    justifyContent: 'center',
                    ...(groupOpen
                      ? {
                          color: '#BEC2DF !important',
                          '& .MuiSvgIcon-root': {
                            width: 16,
                            height: 16,
                            fontSize: 16,
                            color: '#BEC2DF !important',
                          },
                        }
                      : {
                          color: '#fff',
                          '& .MuiSvgIcon-root': {
                            width: 20,
                            height: 20,
                            fontSize: 20,
                            color: '#fff',
                          },
                        }),
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={<HighlightedText text={item.title} query={highlightQuery} />}
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: isActiveItem ? 700 : 400,
                        fontSize: groupOpen ? '12px' : '16px',
                        color: groupOpen ? '#BEC2DF' : 'inherit',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  />
                )}
                {open && (groupOpen ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />)}
              </ListItemButton>
              <Collapse in={groupOpen} timeout={isPinnedOpen ? 'auto' : 0} appear={isPinnedOpen} unmountOnExit>
                <List component="div" disablePadding sx={{ py: '4px' }}>
                  {renderMenuItems(item.subItems, depth + 1)}
                </List>
              </Collapse>
            </>
          ) : (
            <ListItemLink
              to={item.to as string}
              primary={<HighlightedText text={item.title} query={highlightQuery} active={isSearchActive} /> as any}
              icon={item.icon as React.ReactElement}
              showText={open}
              sx={{
                justifyContent,
                background: open ? (isActiveItem ? ACTIVE_ITEM_BACKGROUND : 'transparent') : 'transparent',
                pl: leftPadding,
                pr: open ? '6px' : 0,
                py: '6px',
                borderRadius: '8px',
                ...collapsedActiveStyles,
                ...(isSearchActive && {
                  backgroundColor: 'rgba(255,255,255,0.14)',
                  boxShadow: `inset 0 0 0 1px ${SEARCH_ACTIVE_MARK_BG}`,
                }),
                '& .MuiListItemIcon-root': {
                  minWidth: 'auto',
                  marginRight: iconSpacing,
                  justifyContent: 'center',
                  color: isNestedItem ? '#BEC2DF' : '#fff',
                  '& .MuiSvgIcon-root': {
                    width: isNestedItem ? 16 : 20,
                    height: isNestedItem ? 16 : 20,
                    fontSize: isNestedItem ? 16 : 20,
                    color: isNestedItem ? '#BEC2DF' : '#fff',
                  },
                },
                '& .MuiListItemText-primary': {
                  fontWeight: isActive(item.to ?? '') ? 700 : 400,
                  fontSize: '16px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: open ? 'flex' : 'none',
                  alignItems: 'center',
                },
              }}
            />
          )}
        </Box>
      );
    });
  };

  return (
    <Drawer
      variant="permanent"
      open={open}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      sx={{
        '& .MuiDrawer-paper': {
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // Prevent drawer paper from scrolling
        },
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'flex-end',
          px: open ? 2 : 0.5,
          minHeight: '64px',
        }}
      >
        {open && (
          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700 }}>
            Solution 2.0
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isPinnedOpen ? (
            <IconButton onClick={onClose} sx={{ color: '#fff' }}>
              <ChevronLeftOutlinedIcon />
            </IconButton>
          ) : (
            <IconButton onClick={onPin} sx={{ color: '#fff' }}>
              <PushPinOutlinedIcon />
            </IconButton>
          )}
          <IconButton onClick={toggleAllSections} sx={{ color: '#fff' }}>
            <ExpandOutlinedIcon />
          </IconButton>
        </Box>
      </Toolbar>
      <Divider />
      {open && (
        <Box sx={{ px: 1.5, py: 1 }}>
          <TextField
            value={itemsSearch}
            onChange={(e) => setItemsSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Поиск по меню"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ color: '#BEC2DF', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: itemsSearch && (
                <InputAdornment position="end">
                  {searchResults.length > 0 && (
                    <Typography variant="caption" sx={{ color: '#BEC2DF', mr: 0.5 }}>
                      {activeIndex + 1}/{searchResults.length}
                    </Typography>
                  )}
                  <IconButton size="small" onClick={() => setItemsSearch('')} sx={{ color: '#BEC2DF' }}>
                    <CloseOutlinedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                color: '#fff',
                borderRadius: '8px',
                backgroundColor: 'rgba(255,255,255,0.08)',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                '&.Mui-focused fieldset': { borderColor: SEARCH_ACTIVE_MARK_BG },
              },
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          overflow: 'auto',
          flex: 1,
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none', // IE and Edge
          scrollbarWidth: 'none', // Firefox
          px: open ? 0 : 0,
          py: 0,
        }}
      >
        <Box
          sx={{
            width: '100%',
          }}
        >
          <List component="nav" sx={{ p: 0, px: open ? '4px' : 0 }}>
            {isSearching && visibleItems.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#BEC2DF', px: 2, py: 2 }}>
                Ничего не найдено
              </Typography>
            ) : (
              renderMenuItems(visibleItems, 1)
            )}
          </List>
        </Box>
      </Box>
    </Drawer>
  );
};
